import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { PendingChangesService } from '../../services/pending-changes.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	constructor(
		private router: Router,
		private pendingChanges: PendingChangesService
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Read company_XSRF-TOKEN cookie and add to header for cross-origin requests
		const xsrfToken = this.getCookie('company_XSRF-TOKEN');

		let authReq = req.clone({
			withCredentials: true
		});

		// Add X-XSRF-TOKEN header if cookie exists (for CSRF protection)
		if (xsrfToken) {
			authReq = authReq.clone({
				setHeaders: {
					'X-XSRF-TOKEN': xsrfToken
				}
			});
		}

		return next.handle(authReq).pipe(
			catchError((error: HttpErrorResponse) => {
				// Debug logging for auth errors
				if (error.status === 401 || error.status === 419) {
					const debugInfo = {
						timestamp: new Date().toISOString(),
						status: error.status,
						statusText: error.statusText,
						url: error.url,
						method: req.method,
						xsrfTokenPresent: !!xsrfToken,
						xsrfTokenLength: xsrfToken?.length || 0,
						allCookies: document.cookie
							? document.cookie.split(';').map((c) => c.trim().split('=')[0])
							: [],
						errorMessage: error.error?.message || error.message,
						errorBody: error.error
					};

					console.error(`[AUTH DEBUG] ${error.status} Error:`, debugInfo);
					console.table(debugInfo);
				}

				if (error.status === 401) {
					// Clear local state and redirect to login
					console.warn('[AUTH DEBUG] Logging out user due to 401 response');
					localStorage.removeItem('roles');
					localStorage.removeItem('permissions');
					localStorage.removeItem('loggedInUser');

					const currentUrl = window.location.pathname + window.location.search;
					if (currentUrl && currentUrl !== '/' && !currentUrl.startsWith('/auth')) {
						sessionStorage.setItem('redirect_after_login', currentUrl);
					}

					// Immediately remove all PrimeNG dialog overlays from the DOM before navigating.
					// We cannot use DialogService.close() here because DialogService is provided per-module,
					// meaning the root-level instance has no knowledge of dialogs opened by feature modules.
					// Querying .p-dialog-mask directly targets all PrimeNG dialogs (DynamicDialog, p-dialog,
					// p-confirmDialog) regardless of which module or DialogService instance opened them.
					document.querySelectorAll('.p-dialog-mask').forEach((el) => el.remove());

					// We intentionally use window.location.href instead of Angular's router.navigate() here.
					// router.navigate() keeps the Angular app alive, so any open modals would survive the
					// route change and remain interactive — a security risk after session expiry.
					// window.location.href triggers a full page reload, tearing down the entire Angular app
					// and ensuring no stale in-memory state persists.
					// DOVE-2676 — drop any unsent change so the reload doesn't show the "Leave site?" prompt.
					this.pendingChanges.discardAll();
					window.location.href = '/auth/login';
				}

				if (this.shouldRedirectToNoAccess(error, req)) {
					document.querySelectorAll('.p-dialog-mask').forEach((el) => el.remove());

					if (!this.router.url.startsWith('/no-access')) {
						this.pendingChanges.discardAll();
						this.router.navigate(['/no-access']);
					}
				}

				return throwError(() => error);
			})
		);
	}

	private shouldRedirectToNoAccess(error: HttpErrorResponse, req: HttpRequest<any>): boolean {
		if (error.status !== 403) {
			return false;
		}

		if (error.error?.requiresPasswordReset || error.error?.redirect) {
			return false;
		}

		return !req.url.includes('/auth/');
	}

	private getCookie(name: string): string | null {
		const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
		return match ? decodeURIComponent(match[3]) : null;
	}
}
